import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/constants/brand";
import {
  LayoutDashboard, Mail, Building2, Stethoscope, Send, LogOut, Menu, X, ExternalLink
} from "lucide-react";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/contacts", label: "Contact Messages", icon: Mail },
  { to: "/admin/hospitals", label: "Hospital Inquiries", icon: Building2 },
  { to: "/admin/personnel", label: "Personnel Inquiries", icon: Stethoscope },
  { to: "/admin/newsletter", label: "Newsletter", icon: Send },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#F4F8FE]" data-testid="admin-layout">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded hover:bg-slate-100" onClick={() => setOpen(v => !v)} data-testid="admin-mobile-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/admin" className="flex items-center gap-2">
              <img src={BRAND.logoUrl} alt="HomeCure" className="w-9 h-9 object-contain" />
              <div>
                <div className="font-extrabold text-[#0F2A4A] leading-none">HomeCure Admin</div>
                <div className="text-[10px] text-slate-500">{BRAND.parent}</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:inline-flex text-sm text-slate-600 hover:text-[#3780E0] items-center gap-1.5">
              View site <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <div className="text-sm text-right hidden md:block">
              <div className="font-semibold text-[#0F2A4A]">{admin?.name || "Admin"}</div>
              <div className="text-xs text-slate-500">{admin?.email}</div>
            </div>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-[#E8F0FB] text-[#2563B3] hover:bg-[#C3D9F6]" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} lg:block w-64 shrink-0 bg-white border-r border-slate-200 min-h-[calc(100vh-64px)]`}>
          <nav className="p-3 flex flex-col gap-1">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                    isActive ? "bg-[#3780E0] text-white" : "text-slate-700 hover:bg-[#E8F0FB] hover:text-[#2563B3]"
                  }`
                }
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
